import { type ReactNode } from "react";
import {
  AnnouncementIcon,
  BellSchoolIcon,
  NotificationIcon,
  StarIcon,
} from "vega-icons";

import { tenantConfig } from "@tenant-config";
import { useTheme } from "@vega-core/context/ThemeContext/ThemeContext";

import { AnnouncementTaskView } from "./AnnouncementTaskView";
import { readAnnouncementContent } from "./content";
import { type StepProps } from "./taskRenderers";
import { useCompleteTaskV2 } from "./useCompleteTaskV2";

const BADGE_ICON_CLASS = "h-3.5 w-3.5";

const BADGE_ICONS: Record<string, ReactNode> = {
  ANNOUNCEMENT: <AnnouncementIcon className={BADGE_ICON_CLASS} />,
  BELL: <BellSchoolIcon className={BADGE_ICON_CLASS} />,
  NOTIFICATION: <NotificationIcon className={BADGE_ICON_CLASS} />,
  STAR: <StarIcon className={BADGE_ICON_CLASS} />,
};

export const AnnouncementTask = ({ task, onComplete }: StepProps) => {
  const { completeTask, isPending } = useCompleteTaskV2();
  const { theme } = useTheme();
  const {
    metadata: { name: tenantName },
    branding: {
      logos: { logoLight, logoDark },
    },
  } = tenantConfig;

  const {
    badge,
    badgeIconName,
    primaryButtonText,
    secondaryButtonText,
    primaryActionRoute,
  } = readAnnouncementContent(task.content);

  const logoSrc = theme === "light" ? logoLight : logoDark;
  const badgeIcon =
    (badgeIconName && BADGE_ICONS[badgeIconName]) ?? BADGE_ICONS.ANNOUNCEMENT;

  const handleAcknowledge = () => {
    completeTask(
      {
        taskId: task.id,
        data: { capturedData: { action: "acknowledge" } },
      },
      { onSuccess: () => onComplete() },
    );
  };

  const handlePrimaryAction = () => {
    completeTask(
      {
        taskId: task.id,
        data: {
          capturedData: { action: "primary", route: primaryActionRoute },
        },
      },
      {
        onSuccess: () => {
          if (primaryActionRoute) {
            window.location.assign(primaryActionRoute);
          }
          onComplete();
        },
      },
    );
  };

  return (
    <AnnouncementTaskView
      context={{
        badge,
        badgeIcon,
        title: task.name,
        description: task.taskDescription ?? "",
        primaryButtonText: primaryButtonText ?? "",
        secondaryButtonText: secondaryButtonText ?? "",
      }}
      isSubmitting={isPending}
      logoAlt={tenantName}
      logoSrc={logoSrc}
      onAcknowledge={handleAcknowledge}
      onPrimaryAction={handlePrimaryAction}
    />
  );
};
